"use client"

import React, { useState, useRef } from 'react'
import { FormField } from '../ui/form'
import {
    FieldPath,
    FieldValues,
    Control,
    ControllerRenderProps,
    ControllerFieldState,
} from 'react-hook-form'
import FormTextInputWrapper, { InputGroupProps } from './form-input-wrapper'
import { Checkbox } from '../ui/checkbox'
import { Button } from '../ui/button'
import { ChevronDown, X } from 'lucide-react'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover'
import { cn } from '../../lib/utils'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

type FormMultiSelectInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
    name: TName
    control: Control<TFieldValues>
    inputGroupProps?: InputGroupProps
    options: readonly { label: string; value: string }[]
    placeholder?: string
    searchPlaceholder?: string
    /** Optional class for the trigger button (e.g. rounded-full for pill shape) */
    triggerClassName?: string
    /** Optional class for the placeholder text when no value selected */
    placeholderClassName?: string
    /** Show a "Select All" row at the top of the dropdown (applies to filtered options when search is active) */
    showSelectAll?: boolean
}

function FormMultiSelectInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ 
    name, 
    control,
    inputGroupProps,
    options,
    placeholder = "Select",
    searchPlaceholder = "Search...",
    triggerClassName,
    placeholderClassName,
    showSelectAll = false
}: FormMultiSelectInputProps<TFieldValues, TName>) {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const lastTouchY = useRef(0)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.value.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }: { field: ControllerRenderProps<TFieldValues, TName>, fieldState: ControllerFieldState }) => {
                // Normalize to string[] so option matching works even when form stores numbers
                const rawValue = field.value || []
                const selectedValues: string[] = Array.isArray(rawValue)
                    ? rawValue.map((v: unknown) => (v != null ? String(v) : '')).filter(Boolean)
                    : []

                const handleToggle = (value: string) => {
                    if (isReadOnly) return
                    const valStr = String(value)
                    const newSelection = selectedValues.includes(valStr)
                        ? selectedValues.filter((v) => v !== valStr)
                        : [...selectedValues, valStr]
                    field.onChange(newSelection)
                }

                const handleRemove = (value: string) => {
                    if (isReadOnly) return
                    const newSelection = selectedValues.filter((v) => v !== String(value))
                    field.onChange(newSelection)
                }

                // Match by string so number vs string (e.g. from API) still resolves to labels
                const selectedOptions = selectedValues
                    .map(value => options.find(option => String(option.value) === String(value)))
                    .filter(Boolean)

                return (
                    <FormTextInputWrapper
                        inputGroupProps={inputGroupProps}
                    >
                        <Popover open={isOpen && !isReadOnly} onOpenChange={(open) => !isReadOnly && setIsOpen(open)}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    role="combobox"
                                    aria-expanded={isOpen}
                                    disabled={isReadOnly}
                                    showLoader={false}
                                    className={cn(
                                        "w-full justify-between h-9 border border-input bg-transparent px-3 py-2 text-sm shadow-sm hover:bg-accent/50 hover:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring",
                                        selectedOptions.length > 0 && selectedOptions.length <= 4 && "min-h-9 h-auto whitespace-normal",
                                        selectedOptions.length === 0 && (placeholderClassName ?? "text-muted-foreground"),
                                        triggerClassName ?? "rounded-md"
                                    )}
                                >
                                    {selectedOptions.length > 0 ? (
                                        selectedOptions.length > 4 ? (
                                            <span className="flex-1 text-left truncate">
                                                {selectedOptions.length} selected
                                            </span>
                                        ) : (
                                            <div className="flex flex-wrap gap-1 min-w-0 flex-1">
                                                {selectedOptions.map((option) => (
                                                    <span
                                                        key={option!.value}
                                                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded shrink-0"
                                                    >
                                                        {option!.label}
                                                        {!isReadOnly && (
                                                            <X
                                                                className="h-3 w-3 cursor-pointer hover:text-primary/70 shrink-0"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleRemove(option!.value)
                                                                }}
                                                            />
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        )
                                    ) : (
                                        placeholder
                                    )}
                                    {!isReadOnly && <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="z-[100] w-full p-0" align="start" data-avoid-dialog-close="">
                                <div className="p-2">
                                    <input
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                                    />
                                    <div
                                        className="max-h-60 overflow-y-auto overscroll-contain mt-2"
                                        style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
                                        onWheel={(e) => {
                                            e.stopPropagation()
                                            const el = e.currentTarget
                                            const { scrollTop, scrollHeight, clientHeight } = el
                                            const canScrollDown = scrollTop + clientHeight < scrollHeight
                                            const canScrollUp = scrollTop > 0
                                            if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
                                                e.preventDefault()
                                                el.scrollTop += e.deltaY
                                            }
                                        }}
                                        onTouchStart={(e) => {
                                            if (e.changedTouches.length > 0) {
                                                lastTouchY.current = e.changedTouches[0].clientY
                                            }
                                        }}
                                        onTouchMove={(e) => {
                                            if (e.changedTouches.length === 0) return
                                            const el = e.currentTarget
                                            const touchY = e.changedTouches[0].clientY
                                            const deltaY = lastTouchY.current - touchY
                                            lastTouchY.current = touchY
                                            const { scrollTop, scrollHeight, clientHeight } = el
                                            const maxScroll = Math.max(0, scrollHeight - clientHeight)
                                            if (maxScroll <= 0) return
                                            const canScrollDown = scrollTop < maxScroll
                                            const canScrollUp = scrollTop > 0
                                            const wantsToScroll = (deltaY > 0 && canScrollDown) || (deltaY < 0 && canScrollUp)
                                            if (wantsToScroll && Math.abs(deltaY) > 2) {
                                                e.stopPropagation()
                                                e.preventDefault()
                                                el.scrollTop = Math.max(0, Math.min(maxScroll, el.scrollTop + deltaY))
                                            }
                                        }}
                                    >
                                        {filteredOptions.length > 0 ? (
                                            <div className="space-y-1">
                                                {showSelectAll && (
                                                    <div
                                                        className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer border-b"
                                                        onClick={() => {
                                                            if (isReadOnly) return
                                                            const allFilteredSelected = filteredOptions.every(o =>
                                                                selectedValues.includes(String(o.value))
                                                            )
                                                            field.onChange(allFilteredSelected ? [] : filteredOptions.map(o => String(o.value)))
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={filteredOptions.every(o =>
                                                                selectedValues.includes(String(o.value))
                                                            )}
                                                            className="rounded-[5px] border-primary"
                                                        />
                                                        <span className="text-sm font-medium">Select All</span>
                                                    </div>
                                                )}
                                                {selectedValues.length > 0 && (
                                                    <div
                                                        className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer border-b"
                                                        onClick={() => {
                                                            if (isReadOnly) return
                                                            field.onChange([])
                                                        }}
                                                    >
                                                        <span className="text-sm font-medium text-muted-foreground">Clear all</span>
                                                    </div>
                                                )}
                                                {filteredOptions.map((option) => (
                                                    <div
                                                        key={option.value}
                                                        className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                                                        onClick={() => handleToggle(option.value)}
                                                    >
                                                        <Checkbox
                                                            checked={selectedValues.includes(String(option.value))}
                                                            className="rounded-[5px] border-primary"
                                                        />
                                                        <span className="text-sm text-foreground">
                                                            {option.label}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="p-4 text-center text-sm text-muted-foreground">
                                                No options found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                        {fieldState.error && (
                            <p className="text-sm text-red-500 mt-1">
                                {fieldState.error.message}
                            </p>
                        )}
                    </FormTextInputWrapper>
                )
            }}
        />
    )
}

export default FormMultiSelectInput
